/**
 * The track strip: one row per input on the interface, mapped 1:1.
 *
 * ## Every meter is drawn by one loop
 *
 * There is a canvas per row, but a single `requestAnimationFrame` loop for all
 * of them, and it reads the engine's shared meter buffer directly. No level
 * passes through React state. Thirty-two channels at the display rate is around
 * two thousand state updates a second, and React would spend a core deciding
 * that a `<div>` had not changed.
 *
 * The loop also stops itself when the document is hidden, which the browser
 * would do anyway — the note is here because it is the reason a meter can look
 * frozen for a moment when a tab comes back, while the audio never was.
 */

import { useEffect, useRef } from 'react';
import { recorder, SILENCE_DB } from '../lib/recorder';
import { formatDb, meterPosition } from '../lib/format';
import { levelColour, meterColours } from '../lib/theme';
import type { Level, Track } from '../types';

interface Props {
  tracks: Track[];
  /** Inputs the open device actually has. 0 when nothing is open. */
  channels: number;
  recording: boolean;
  onChange: (input: number, patch: Partial<Track>) => void;
  onClearSolo: () => void;
}

export function TrackList({ tracks, channels, recording, onChange, onClearSolo }: Props) {
  const canvases = useRef<(HTMLCanvasElement | null)[]>([]);
  const readouts = useRef<(HTMLSpanElement | null)[]>([]);
  const anySolo = tracks.some((t) => t.soloed);

  useEffect(() => {
    let raf = 0;
    const level: Level = { peakDb: SILENCE_DB, rmsDb: SILENCE_DB, holdDb: SILENCE_DB, clipped: false };
    const c = meterColours(document.documentElement);
    let clipGeneration = recorder.clipGeneration;

    const draw = () => {
      raf = requestAnimationFrame(draw);
      const cleared = recorder.clipGeneration !== clipGeneration;
      if (cleared) clipGeneration = recorder.clipGeneration;

      for (let i = 0; i < canvases.current.length; i++) {
        const canvas = canvases.current[i];
        if (!canvas) continue;
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;

        // Size to the device pixel ratio here rather than in a resize handler:
        // the row height is fixed, the check is two comparisons, and it picks
        // up a window dragged to a second screen with a different ratio.
        const dpr = window.devicePixelRatio || 1;
        const w = Math.round(canvas.clientWidth * dpr);
        const h = Math.round(canvas.clientHeight * dpr);
        if (canvas.width !== w || canvas.height !== h) {
          canvas.width = w;
          canvas.height = h;
        }

        recorder.level(i, level);
        ctx.fillStyle = c.back;
        ctx.fillRect(0, 0, w, h);

        // The two marks worth having on a bar this small: -18 is comfortable,
        // -6 is close. Same scale function as the bridge, so a level that sits
        // on a line here sits on the same line there.
        ctx.fillStyle = c.grid;
        for (const mark of [-18, -6]) {
          ctx.fillRect(Math.round(meterPosition(mark) * w), 0, dpr, h);
        }

        // Peak bar, coloured by where its top is rather than as a gradient: the
        // point of the colour is to say which band the level is in, and a
        // gradient says that only at the very tip while implying it about the
        // whole bar.
        const peak = meterPosition(level.peakDb) * w;
        ctx.fillStyle = levelColour(level.peakDb, c);
        ctx.fillRect(0, 0, peak, h);

        // RMS as a second, shorter bar inside the first. Peak says whether it
        // will clip; RMS says how loud it is. Both matter and they are not the
        // same number.
        const rms = meterPosition(level.rmsDb) * w;
        ctx.fillStyle = c.rms;
        ctx.fillRect(0, h * 0.35, rms, h * 0.3);

        if (level.holdDb > SILENCE_DB) {
          const hold = meterPosition(level.holdDb) * w;
          ctx.fillStyle = c.hold;
          ctx.fillRect(Math.max(0, hold - dpr), 0, dpr * 1.5, h);
        }

        // The numeric readout is written straight into the DOM node, for the
        // same reason the bar is drawn on a canvas: it changes sixty times a
        // second on every channel, and it is not something React should hear
        // about. The clip flag latches until it is cleared, so it is set here
        // and never unset by this loop.
        const out = readouts.current[i];
        if (out) {
          const text = formatDb(level.peakDb);
          if (out.textContent !== text) out.textContent = text;
          if (cleared) out.classList.remove('clip');
          if (level.clipped) out.classList.add('clip');
        }
      }
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Track names, arm state and mix settings persist, so reconnecting the same
  // interface keeps the labelling. The *rows* must not persist with them: a
  // list of tracks on a page with no device open describes hardware that is not
  // there, with meters that can never move.
  if (channels === 0 || tracks.length === 0) {
    return <p className="empty">Choose an interface and its inputs appear here, one track each.</p>;
  }

  return (
    <div className="tracks">
      <div className="tracks-head">
        <span className="col-num">In</span>
        <span className="col-name">Track</span>
        <span className="col-arm">Rec</span>
        <span className="col-meter">Level</span>
        <span className="col-mix">Playback</span>
        {anySolo && (
          <button type="button" className="link" onClick={onClearSolo}>
            clear solo
          </button>
        )}
      </div>

      {tracks.map((t, i) => {
        return (
          <div className={`track${t.armed ? '' : ' disarmed'}`} key={t.input}>
            <span className="col-num">{t.input + 1}</span>

            <input
              className="col-name"
              value={t.name}
              aria-label={`Name of input ${t.input + 1}`}
              onChange={(e) => onChange(t.input, { name: e.target.value })}
            />

            <button
              type="button"
              className={`arm${t.armed ? ' on' : ''}`}
              aria-pressed={t.armed}
              // Arming during a take would have to open a file mid-recording and
              // the track would start late against every other one. The take is
              // fixed at the moment record is pressed.
              disabled={recording}
              title={recording ? 'The armed set is fixed for the length of a take' : 'Record this input'}
              onClick={() => onChange(t.input, { armed: !t.armed })}
            >
              ●
            </button>

            <div className="col-meter">
              <canvas
                ref={(el) => {
                  canvases.current[i] = el;
                }}
                className="meter"
              />
              <span
                className="readout"
                ref={(el) => {
                  readouts.current[i] = el;
                }}
              >
                -&#8734;
              </span>
            </div>

            <div className="col-mix">
              <input
                type="range"
                min={-40}
                max={12}
                step={0.5}
                value={t.gainDb}
                aria-label={`Playback gain, input ${t.input + 1}`}
                onChange={(e) => onChange(t.input, { gainDb: Number(e.target.value) })}
              />
              <input
                type="range"
                min={-1}
                max={1}
                step={0.02}
                value={t.pan}
                aria-label={`Playback pan, input ${t.input + 1}`}
                onChange={(e) => onChange(t.input, { pan: Number(e.target.value) })}
              />
              <button
                type="button"
                className={`tiny${t.muted ? ' on' : ''}`}
                aria-pressed={t.muted}
                onClick={() => onChange(t.input, { muted: !t.muted })}
              >
                M
              </button>
              <button
                type="button"
                className={`tiny solo${t.soloed ? ' on' : ''}`}
                aria-pressed={t.soloed}
                onClick={() => onChange(t.input, { soloed: !t.soloed })}
              >
                S
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
